// src/components/BlogManagement.js

import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../style/ProductManagement.css";
import { ReactComponent as SearchIcon } from "../icons/search.svg";
import { db } from "../firebase-config";
import { collection, getDocs, query, orderBy } from "firebase/firestore";

const BlogManagement = () => {
  const today = new Date().toLocaleDateString();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const postsCollection = collection(db, "designJournal");
        const q = query(postsCollection, orderBy("createdAt", "desc"));
        const querySnapshot = await getDocs(q);

        const fetchedPosts = querySnapshot.docs.map((doc, index) => ({
          id: doc.id,
          memoNumber: querySnapshot.docs.length - index,
          ...doc.data(),
        }));

        setPosts(fetchedPosts);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching blog posts:", error);
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  const handleAddBlogClick = () => {
    navigate("/add-blog", {
      state: {
        from: location.pathname,
        activeComponent: "blogManagement",
      },
    });
  };

  const handlePostClick = (post) => {
    navigate("/add-blog", {
      state: {
        post: post,
        from: location.pathname,
        activeComponent: "blogManagement",
      },
    });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "--/--/----";
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      if (isNaN(date.getTime())) return "--/--/----";
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return "--/--/----";
    }
  };

  const truncateText = (text, maxLength = 60) => {
    if (!text) return "";
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  const filteredPosts = posts.filter(
    (post) =>
      post.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.content?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <div className="product-management-container">
      <div className="header">
        <div className="date">{today}</div>
        <div className="search-container">
          <SearchIcon className="icon" />
          <input
            type="text"
            placeholder="Search blogs"
            className="search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>
      <div className="title-section">
        <div className="product-management-title">Blog Management</div>
        <div className="add-product-container">
          <div className="add-product-box" onClick={handleAddBlogClick}>
            ADD BLOG POST
          </div>
        </div>
      </div>

      {/* Blog Posts List */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0",
          marginTop: "20px",
        }}
      >
        {loading ? (
          <div style={{ padding: "20px", color: "#666" }}>Loading...</div>
        ) : filteredPosts.length === 0 ? (
          <div style={{ padding: "20px", color: "#666" }}>
            No blog posts yet. Click "ADD BLOG POST" to create one.
          </div>
        ) : (
          filteredPosts.map((post) => (
            <div
              key={post.id}
              onClick={() => handlePostClick(post)}
              style={{
                display: "flex",
                flexDirection: "row",
                padding: "15px 0",
                borderBottom: "1px solid #eee",
                cursor: "pointer",
                gap: "15px",
                alignItems: "flex-start",
              }}
            >
              {/* Thumbnail */}
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  minWidth: "80px",
                  backgroundColor: "#f0f0f0",
                  borderRadius: "4px",
                  overflow: "hidden",
                }}
              >
                {post.images && post.images.length > 0 ? (
                  <img
                    src={post.images[0]}
                    alt={post.title || "Blog post"}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#999",
                      fontSize: "11px",
                    }}
                  >
                    No image
                  </div>
                )}
              </div>

              {/* Post Info */}
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                }}
              >
                {/* Header row: memo and date */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontWeight: "500",
                      color: "#333",
                      fontFamily: "Georgia, serif",
                      fontSize: "14px",
                    }}
                  >
                    memo #{post.memoNumber}
                  </span>
                  <span
                    style={{
                      color: "#666",
                      fontSize: "13px",
                      fontFamily: "Georgia, serif",
                    }}
                  >
                    {formatDate(post.createdAt)}
                  </span>
                </div>

                {post.title && (
                  <div
                    style={{
                      fontWeight: "500",
                      color: "#333",
                      fontSize: "14px",
                      fontFamily: "Georgia, serif",
                    }}
                  >
                    {post.title}
                  </div>
                )}

                {/* Content preview */}
                <div
                  style={{
                    color: "#666",
                    fontSize: "13px",
                    fontFamily: "Georgia, serif",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {truncateText(post.content, 70) || "No content"}
                </div>

                {/* Image count */}
                <div
                  style={{
                    color: "#999",
                    fontSize: "12px",
                    fontFamily: "Georgia, serif",
                  }}
                >
                  {post.images?.length || 0} image(s)
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default BlogManagement;
